import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getAuthToken } from "../../lib/api/tokenStore";
import { supplierPath } from "../detail/detailRoutes";

import useSupplierSave from "./useSupplierSave";
import SectionHeader from "../accessories/components/SectionHeader";
import SavedSupplierTile from "./components/SavedSupplierTile";
import SavedSuppliersEmpty from "./components/SavedSuppliersEmpty";
import {
  SavedCountSkeleton,
  SavedSuppliersGridSkeleton,
} from "./components/SavedSuppliersSkeletons";

import "../accessories/styles.css";
import "./styles.css";

export const PAGE_SIZE = 20;

const countLabel = (count) => `${count} ${count === 1 ? "supplier" : "suppliers"} saved`;

const SavedSuppliersRoute = () => {
  const navigate = useNavigate();
  const save = useSupplierSave({ enabled: Boolean(getAuthToken()) });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { refetch } = save;

  useEffect(() => {
    refetch();

    const onVisible = () => {
      if (document.visibilityState === "visible") refetch();
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refetch]);

  const visibleIds = useMemo(
    () => save.savedTargetIds.slice(0, visibleCount),
    [save.savedTargetIds, visibleCount]
  );

  const openSupplier = useCallback(
    (supplier) => navigate(supplierPath({ supplierId: supplier.id })),
    [navigate]
  );

  const browseSuppliers = useCallback(() => navigate("/"), [navigate]);

  const hasIds = save.savedCount > 0;
  const showCount = save.isLoading || hasIds;

  const renderBody = () => {
    if (save.isError && !hasIds) {
      return <SavedSuppliersEmpty kind="error" onRetry={refetch} />;
    }

    if (save.isLoading) return <SavedSuppliersGridSkeleton />;

    if (!hasIds) return <SavedSuppliersEmpty onBrowse={browseSuppliers} />;

    return (
      <>
        <ul className="acc-grid">
          {visibleIds.map((supplierId) => (
            <SavedSupplierTile
              key={supplierId}
              supplierId={supplierId}
              pending={save.isPending({ id: supplierId })}
              onOpen={openSupplier}
              onToggleSave={save.toggleSaved}
            />
          ))}
        </ul>

        {visibleCount < save.savedCount && (
          <div className="acc-more">
            <button
              type="button"
              className="acc-more-button"
              onClick={() => setVisibleCount((current) => current + PAGE_SIZE)}
            >
              Load more
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="sv-screen">
      <header className="sv-pagehead">
        <h1 className="sv-pagetitle">Saved Suppliers</h1>

        {showCount && (
          <p className="sv-count" aria-live="polite">
            {save.isLoading ? <SavedCountSkeleton /> : countLabel(save.savedCount)}
          </p>
        )}
      </header>

      <div className="sv-band">
        <SectionHeader title="Your Saved Suppliers" />

        {renderBody()}
      </div>
    </div>
  );
};

export default SavedSuppliersRoute;
